import {Box, CircularProgress, Typography} from '@mui/material';
import * as Plot from '@observablehq/plot';
import {useEffect, useRef} from 'react';
import {Progress} from '@cp/model';

interface TopicPerformanceChartProps {
  progressList: Progress[];
  loading: boolean;
}

export default function TopicPerformanceChart({progressList, loading}: TopicPerformanceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || !containerRef.current) return;

    if (!progressList || progressList.length === 0) {
      containerRef.current.innerHTML = '';
      return;
    }

    // Calculate average score per topic across all modules
    const topicScores = new Map<string, {score: number; count: number; total: number; correct: number}>();

    progressList.forEach((progress) => {
      progress.scores.forEach((score) => {
        const existing = topicScores.get(score.topic);
        if (existing) {
          existing.score += score.score;
          existing.count += 1;
          existing.total += score.total;
          existing.correct += score.correct;
        } else {
          topicScores.set(score.topic, {
            score: score.score,
            count: 1,
            total: score.total,
            correct: score.correct,
          });
        }
      });
    });

    // Convert to chart data
    const chartData = Array.from(topicScores.entries())
      .map(([topic, data]) => ({
        topic: topic.length > 15 ? `${topic.substring(0, 15)}...` : topic,
        fullTopic: topic,
        averageScore: Math.round((data.score / data.count) * 10) / 10,
        accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0,
        questionsAnswered: data.total,
      }))
      .sort((a, b) => b.averageScore - a.averageScore)
      .slice(0, 10); // Show top 10 topics

    const plotOptions: Plot.PlotOptions = {
      marks: [
        Plot.gridY({stroke: '#f0f0f0', strokeDasharray: '3,3'}),
        Plot.barY(chartData, {
          x: 'topic',
          y: 'averageScore',
          fill: '#1976d2',
          tip: {
            format: {
              x: (d: string) => {
                const data = chartData.find((item) => item.topic === d);
                return data?.fullTopic || d;
              },
              y: (d: number) => `Score: ${d}/5`,
            },
          },
        }),
      ],
      x: {
        label: null,
        type: 'band',
      },
      y: {
        label: 'Score (out of 5)',
        domain: [0, 5],
        type: 'linear',
      },
      marginTop: 20,
      marginRight: 30,
      marginBottom: 80,
      marginLeft: 20,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [progressList, loading]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!progressList || progressList.length === 0) {
    return (
      <Box sx={{textAlign: 'center', py: 4}}>
        <Typography variant="body1" color="text.secondary">
          No progress data available yet. Start studying to see your performance!
        </Typography>
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{height: 300}} />;
}
